import type { IconName } from '@/components/ui';

export type AuditAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'consent'
  | 'merge'
  | 'sync'
  | 'import'
  | 'export';

export interface AuditActionMeta {
  label: string;
  color: string;
  icon: IconName;
}

export const ACTION_META: Record<AuditAction, AuditActionMeta> = {
  create: { label: 'Created', color: '#0F9D6B', icon: 'plus' },
  update: { label: 'Updated', color: '#1F47AB', icon: 'edit' },
  delete: { label: 'Soft-deleted', color: '#C0392B', icon: 'trash' },
  consent: { label: 'Consent', color: '#D97706', icon: 'shield' },
  merge: { label: 'Merged', color: '#7A4FB5', icon: 'link' },
  sync: { label: 'Synced', color: '#0E8E8E', icon: 'history' },
  import: { label: 'Imported', color: '#475569', icon: 'upload' },
  export: { label: 'Exported', color: '#475569', icon: 'download' },
};

const FALLBACK_META: AuditActionMeta = {
  label: 'Event',
  color: '#475569',
  icon: 'info',
};

export function actionMeta(action?: string): AuditActionMeta {
  if (!action) return FALLBACK_META;
  const known = action.toLowerCase() as AuditAction;
  return ACTION_META[known] ?? { ...FALLBACK_META, label: capitalize(action) };
}

export function isKnownAction(action: string | undefined): action is AuditAction {
  return !!action && action in ACTION_META;
}

/** MUI-color shim retained for legacy call-sites. */
export function getActionColor(
  action?: string,
): 'success' | 'primary' | 'error' | 'warning' | 'info' | 'default' {
  switch (action?.toLowerCase()) {
    case 'create':
      return 'success';
    case 'update':
      return 'primary';
    case 'delete':
      return 'error';
    case 'consent':
      return 'warning';
    case 'sync':
      return 'info';
    default:
      return 'default';
  }
}

const DAY_MS = 86_400_000;

export type DayBucket = 'Today' | 'Yesterday' | 'Earlier this week' | 'This month' | 'Older';

export function dayBucket(ts: string, now = Date.now()): DayBucket {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return 'Older';
  const startOfDay = (t: number) => {
    const dt = new Date(t);
    dt.setHours(0, 0, 0, 0);
    return dt.getTime();
  };
  const diffDays = Math.floor((startOfDay(now) - startOfDay(d.getTime())) / DAY_MS);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return 'Earlier this week';
  if (diffDays < 30) return 'This month';
  return 'Older';
}

export const DAY_BUCKET_ORDER: DayBucket[] = [
  'Today',
  'Yesterday',
  'Earlier this week',
  'This month',
  'Older',
];

export function timeAgo(ts: string, now = Date.now()): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const diffMin = (now - d.getTime()) / 60000;
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
  if (diffMin < 1440) return `${Math.round(diffMin / 60)} h ago`;
  if (diffMin < 1440 * 7) return `${Math.round(diffMin / 1440)} d ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function formatHourMinute(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
